import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone, Check, ArrowRight, Wrench, Building2, Grid3X3, Droplets,
  CloudRain, Sparkles, Snowflake, Truck, Hammer, Layers,
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { Seo } from "@/components/Seo";
import { getService, SERVICES_DATA } from "@/data/services";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/scroll";

const ICONS = { Wrench, Building2, Grid3X3, Droplets, CloudRain, Sparkles, Snowflake, Truck, Hammer, Layers };

const Reveal = ({ children, delay = 0, ...props }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay }}
    {...props}
  >
    {children}
  </motion.div>
);

export default function ServicePage({ slug }) {
  const s = getService(slug);
  const navigate = useNavigate();
  if (!s) return null;
  const Icon = ICONS[s.icon];
  const related = SERVICES_DATA.filter((r) => s.related.includes(r.slug));

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: s.name,
        description: s.metaDescription,
        url: `https://terbeck-service.de/${s.slug}`,
        areaServed: ["Gronau (Westf.)", "Epe", "Ochtrup", "Ahaus", "Kreis Borken"],
        provider: {
          "@type": "LocalBusiness",
          name: "Terbeck Service GbR",
          telephone: "+4915150778186",
          email: "info@terbeck-service.de",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Riekenhofweg 29",
            postalCode: "48599",
            addressLocality: "Gronau (Westf.)",
            addressCountry: "DE",
          },
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: s.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Startseite", item: "https://terbeck-service.de/" },
          { "@type": "ListItem", position: 2, name: "Leistungen", item: "https://terbeck-service.de/#leistungen" },
          { "@type": "ListItem", position: 3, name: s.name, item: `https://terbeck-service.de/${s.slug}` },
        ],
      },
    ],
  };

  return (
    <div className="bg-ink text-white min-h-screen flex flex-col">
      <Seo title={s.seoTitle} description={s.metaDescription} path={`/${s.slug}`} jsonLd={jsonLd} />
      <Header />
      <main className="flex-1">
        <section className="mx-auto max-w-7xl px-5 md:px-8 pt-36 pb-16 md:pt-44 md:pb-24">
          <Reveal>
            <nav aria-label="Breadcrumb" className="mb-6 text-xs font-bold tracking-[0.2em] text-white/40">
              <Link to="/" className="hover:text-brand-bright transition-colors duration-300">STARTSEITE</Link>
              <span className="mx-2 text-brand">/</span>
              <span className="text-white/70">{s.name.toUpperCase()}</span>
            </nav>
            <p className="mb-4 text-xs font-bold tracking-[0.3em] text-brand-bright">
              LEISTUNGEN · GRONAU (WESTF.) & KREIS BORKEN
            </p>
            <h1 className="font-display text-3xl sm:text-5xl md:text-6xl font-bold uppercase tracking-tighter leading-[1.05] text-white max-w-4xl">
              {s.h1}
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-white/70">{s.intro}</p>
          </Reveal>

          <Reveal delay={0.15} className="relative mt-12">
            <div className="absolute -top-4 -left-4 h-full w-full rounded-2xl border-2 border-brand" aria-hidden="true" />
            <img
              src={s.image}
              alt={s.imageAlt}
              className={`relative h-[280px] md:h-[440px] w-full rounded-2xl object-cover ${s.imgPos || "object-center"}`}
              loading="lazy"
            />
          </Reveal>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {s.gallery.map((g, i) => (
              <Reveal key={g.src} delay={i * 0.1}>
                <img
                  src={g.src}
                  alt={g.alt}
                  className="h-[240px] md:h-[320px] w-full rounded-2xl border border-white/10 object-cover"
                  loading="lazy"
                />
              </Reveal>
            ))}
          </div>
        </section>

        <section className="border-t border-white/10 bg-surface">
          <div className="mx-auto max-w-7xl px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <Reveal>
              <h2 className="font-display text-2xl md:text-4xl font-bold uppercase tracking-tight text-white">
                {s.name} durch Terbeck Service
              </h2>
              {s.beschreibung.map((p, i) => (
                <p key={i} className="mt-5 text-sm md:text-base leading-relaxed text-white/60">{p}</p>
              ))}
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display text-2xl md:text-4xl font-bold uppercase tracking-tight text-white">
                Ihre Vorteile
              </h2>
              <ul className="mt-6 space-y-4">
                {s.vorteile.map((v) => (
                  <li key={v} className="flex items-start gap-3 text-sm md:text-base text-white/70">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand/15 text-brand-bright ring-1 ring-brand/30">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    {v}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>

        {(s.surfaces || s.finishes) && (
          <section className="mx-auto max-w-7xl px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {s.surfaces && (
              <Reveal>
                <h2 className="font-display text-2xl md:text-4xl font-bold uppercase tracking-tight text-white">
                  Für diese Flächen
                </h2>
                <p className="mt-4 text-sm md:text-base leading-relaxed text-white/60">
                  Unsere Epoxidharz-Bodenbeschichtungen eignen sich für private und gewerbliche Nutzflächen:
                </p>
                <ul className="mt-6 grid grid-cols-2 gap-3">
                  {s.surfaces.map((f) => (
                    <li
                      key={f}
                      className="rounded-xl border border-white/10 bg-surface px-4 py-3.5 text-sm font-semibold text-white/80 transition-colors duration-300 hover:border-brand/50"
                    >
                      {f}
                    </li>
                  ))}
                </ul>
              </Reveal>
            )}
            {s.finishes && (
              <Reveal delay={0.1}>
                <h2 className="font-display text-2xl md:text-4xl font-bold uppercase tracking-tight text-white">
                  Optiken & Ausführungen
                </h2>
                <p className="mt-4 text-sm md:text-base leading-relaxed text-white/60">
                  Ihr Boden soll zu Ihrem Raum passen – möglich sind unter anderem:
                </p>
                <ul className="mt-6 space-y-4">
                  {s.finishes.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm md:text-base text-white/70">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand/15 text-brand-bright ring-1 ring-brand/30">
                        <Check className="h-3.5 w-3.5" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
              </Reveal>
            )}
          </section>
        )}

        <section className="mx-auto max-w-7xl px-5 md:px-8 py-16 md:py-24">
          <Reveal>
            <h2 className="font-display text-2xl md:text-4xl font-bold uppercase tracking-tight text-white">
              Typische Anwendungsfälle
            </h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {s.anwendungsfaelle.map((a, i) => (
              <Reveal key={a} delay={i * 0.06}>
                <div className="flex h-full items-center gap-4 rounded-xl border border-white/10 bg-surface p-5">
                  <span className="font-display text-xl font-extrabold text-brand">0{i + 1}</span>
                  <p className="text-sm md:text-base text-white/70">{a}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16">
            <h2 className="font-display text-2xl md:text-4xl font-bold uppercase tracking-tight text-white">
              So läuft es ab
            </h2>
          </Reveal>
          <ol className="mt-8 space-y-0">
            {s.ablauf.map((step, i) => (
              <Reveal key={step} delay={i * 0.05}>
                <li className="flex items-start gap-6 border-t border-white/10 py-6 last:border-b">
                  <span className="font-display text-2xl md:text-3xl font-extrabold text-brand/60 w-12 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-sm md:text-base text-white/70 pt-1">{step}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </section>

        <section className="border-t border-white/10 bg-surface">
          <div className="mx-auto max-w-4xl px-5 md:px-8 py-16 md:py-24">
            <Reveal>
              <h2 className="font-display text-2xl md:text-4xl font-bold uppercase tracking-tight text-white">
                Häufige Fragen
              </h2>
            </Reveal>
            <div className="mt-8 space-y-6">
              {s.faq.map((f) => (
                <Reveal key={f.q}>
                  <div className="rounded-xl border border-white/10 bg-black/40 p-6">
                    <h3 className="font-display text-lg font-bold text-white">{f.q}</h3>
                    <p className="mt-3 text-sm md:text-base leading-relaxed text-white/60">{f.a}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden">
          <div className="absolute -left-40 top-1/4 h-[360px] w-[360px] rounded-full bg-brand/10 blur-[130px]" aria-hidden="true" />
          <div className="relative mx-auto max-w-7xl px-5 md:px-8 py-16 md:py-24">
            <Reveal>
              <h2 className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tighter text-white leading-[1.05]">
                {s.name} in Gronau anfragen –<br />
                <span className="text-brand-bright">kostenlos & unverbindlich.</span>
              </h2>
              <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-4">
                <a
                  data-testid="service-call-button"
                  href={PHONE_TEL}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-brand px-8 py-4 text-sm font-bold tracking-wider text-black hover:bg-brand-bright transition-colors duration-300"
                >
                  <Phone className="h-4 w-4" /> JETZT ANRUFEN — {PHONE_DISPLAY}
                </a>
                <button
                  data-testid="service-offer-button"
                  onClick={() => navigate("/", { state: { scrollTo: "kontakt" } })}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-white/30 px-8 py-4 text-sm font-bold tracking-wider text-white hover:bg-white hover:text-black transition-colors duration-300"
                >
                  ANGEBOT ANFRAGEN <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="mt-16 border-t border-white/10 pt-10">
              <p className="mb-6 text-xs font-bold tracking-[0.3em] text-white/40">PASSENDE LEISTUNGEN</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {related.map((r) => {
                  const RIcon = ICONS[r.icon];
                  return (
                    <Link
                      key={r.slug}
                      data-testid={`related-${r.slug}`}
                      to={`/${r.slug}`}
                      className="group flex items-center gap-4 rounded-xl border border-white/10 bg-surface p-5 transition-[border-color,transform] duration-300 hover:border-brand/50 hover:-translate-y-0.5"
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand-bright ring-1 ring-brand/30 transition-colors duration-300 group-hover:bg-brand group-hover:text-black">
                        <RIcon className="h-5 w-5" />
                      </span>
                      <span className="text-sm font-bold text-white group-hover:text-brand-bright transition-colors duration-300">
                        {r.name}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
